type RouteChangeListener = () => void;

// Single source of route changes for the whole SDK. history.pushState /
// replaceState + popstate are patched ONCE (when the first subscriber arrives)
// and the originals are restored when the LAST subscriber leaves — so repeated
// start/stop or multiple consumers never stack wrappers or leak the patch (the
// bug when scroll-capture and navigation-capture each patched history alone).
const listeners = new Set<RouteChangeListener>();
let originalPushState: typeof history.pushState | null = null;
let originalReplaceState: typeof history.replaceState | null = null;

function notify(): void {
  for (const listener of listeners) {
    try {
      listener();
    } catch {
      // noop — one listener must not break the others or the navigation itself
    }
  }
}

function install(): void {
  originalPushState = history.pushState;
  originalReplaceState = history.replaceState;
  history.pushState = function (...args: Parameters<typeof history.pushState>) {
    originalPushState?.apply(this, args);
    notify();
  };
  history.replaceState = function (
    ...args: Parameters<typeof history.replaceState>
  ) {
    originalReplaceState?.apply(this, args);
    notify();
  };
  window.addEventListener('popstate', notify);
}

function uninstall(): void {
  if (originalPushState) history.pushState = originalPushState;
  if (originalReplaceState) history.replaceState = originalReplaceState;
  window.removeEventListener('popstate', notify);
  originalPushState = null;
  originalReplaceState = null;
}

/** Subscribe to SPA route changes (pushState / replaceState / popstate). */
export function onRouteChange(listener: RouteChangeListener): () => void {
  if (listeners.size === 0) install();
  listeners.add(listener);
  return () => {
    if (!listeners.delete(listener)) return;
    if (listeners.size === 0) uninstall();
  };
}
